'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronUp, ChevronDown } from 'lucide-react'

const SCROLL_TOP_THRESHOLD = 300
const SCROLL_BOTTOM_MARGIN = 300
const BUTTON_SIZE = 44
const RING_RADIUS = 17
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

export function ScrollToTop() {
  const [showUp, setShowUp] = useState(false)
  const [showDown, setShowDown] = useState(false)
  const [progress, setProgress] = useState(0)
  const rafRef = useRef<number>(0)

  const updateState = useCallback(() => {
    const scrollY = window.scrollY
    const docHeight = document.documentElement.scrollHeight
    const viewportHeight = window.innerHeight
    const maxScroll = docHeight - viewportHeight

    setShowUp(scrollY > SCROLL_TOP_THRESHOLD)
    setShowDown(scrollY < maxScroll - SCROLL_BOTTOM_MARGIN)

    const pct = maxScroll > 0 ? Math.min(scrollY / maxScroll, 1) : 0
    setProgress(pct)
  }, [])

  useEffect(() => {
    // Use rAF for initial state to avoid synchronous setState in effect
    rafRef.current = requestAnimationFrame(updateState)
    const handleScroll = () => {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(updateState)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(rafRef.current)
    }
  }, [updateState])

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const scrollToBottom = useCallback(() => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    })
  }, [])

  const strokeDashoffset = RING_CIRCUMFERENCE * (1 - progress)

  const showAny = showUp || showDown

  return (
    <AnimatePresence>
      {showAny && (
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="fixed bottom-24 right-4 z-40 flex flex-col gap-2 sm:right-6 sm:bottom-24"
        >
          {/* Scroll to top button with SVG progress ring */}
          <AnimatePresence>
            {showUp && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToTop}
                className="group relative flex items-center justify-center shadow-lg transition-shadow hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                style={{ width: BUTTON_SIZE, height: BUTTON_SIZE }}
                aria-label="Наверх"
              >
                {/* Background circle */}
                <svg
                  className="absolute inset-0"
                  width={BUTTON_SIZE}
                  height={BUTTON_SIZE}
                  viewBox={`0 0 ${BUTTON_SIZE} ${BUTTON_SIZE}`}
                >
                  {/* Track */}
                  <circle
                    cx={BUTTON_SIZE / 2}
                    cy={BUTTON_SIZE / 2}
                    r={RING_RADIUS}
                    fill="none"
                    stroke="oklch(0.92 0.005 80)"
                    strokeWidth="3"
                  />
                  {/* Progress arc */}
                  <circle
                    cx={BUTTON_SIZE / 2}
                    cy={BUTTON_SIZE / 2}
                    r={RING_RADIUS}
                    fill="none"
                    stroke="var(--brand)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={RING_CIRCUMFERENCE}
                    strokeDashoffset={strokeDashoffset}
                    transform={`rotate(-90 ${BUTTON_SIZE / 2} ${BUTTON_SIZE / 2})`}
                    className="transition-[stroke-dashoffset] duration-150"
                  />
                </svg>
                <ChevronUp className="size-5 text-brand dark:text-brand-light relative z-10" />
              </motion.button>
            )}
          </AnimatePresence>

          {/* Scroll to bottom button */}
          <AnimatePresence>
            {showDown && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToBottom}
                className="flex items-center justify-center rounded-full bg-brand text-primary-foreground shadow-lg transition-shadow hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                style={{ width: BUTTON_SIZE, height: BUTTON_SIZE }}
                aria-label="Вниз"
              >
                <ChevronDown className="size-5" />
              </motion.button>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
